export interface BluetoothPrinter {
  device: BluetoothDevice;
  name: string;
  id: string;
}

export type WriteCharacteristic = BluetoothRemoteGATTCharacteristic;

// Common printer service UUIDs to try
const PRINTER_SERVICE_UUIDS = [
  '000018f0-0000-1000-8000-00805f9b34fb',
  '0000fff0-0000-1000-8000-00805f9b34fb', 
  '49535343-fe7d-4ae5-8fa9-9fafd205e455',
  'e7810a71-73ae-499d-8c15-faa9aef0c3f2',
  '6e400001-b5a3-f393-e0a9-e50e24dcca9e',
];

/**
 * Convert a string to an ArrayBuffer
 */
export function stringToArrayBuffer(str: string): ArrayBuffer {
  const encoder = new TextEncoder();
  return encoder.encode(str).buffer;
}

/**
 * Convert a number array to an ArrayBuffer
 */
export function numberArrayToArrayBuffer(arr: number[]): ArrayBuffer {
  const uint8Array = new Uint8Array(arr);
  return uint8Array.buffer;
}

/**
 * Check if Web Bluetooth API is supported in the current browser
 */
export function isWebBluetoothSupported(): boolean {
  return typeof navigator !== 'undefined' && 'bluetooth' in navigator;
}

/**
 * Check if a Bluetooth device is currently connected
 */
export function isDeviceConnected(device: BluetoothDevice): boolean {
  return device.gatt?.connected || false;
}

/**
 * Scan for available Bluetooth printers - SIMPLIFIED VERSION
 */
export async function scanForPrinters(
  acceptAllDevices = true
): Promise<BluetoothPrinter | null> {
  console.log('[Bluetooth] scanForPrinters called, acceptAllDevices:', acceptAllDevices);

  if (!navigator.bluetooth) {
    throw new Error('Web Bluetooth is not supported');
  }

  try {
    let device: BluetoothDevice;

    // Include printer services so we can access them after connecting
    const optionalServices = [
      ...PRINTER_SERVICE_UUIDS,
      'battery_service',
      'device_information',
      'generic_access',
    ];

    if (acceptAllDevices) {
      // Simple mode - show all devices
      console.log('[Bluetooth] Requesting ALL devices...');
      device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices,
      });
    } else {
      // Filtered mode - show only printers
      // Windows requires 'services' in filters for compatibility
      // Create filters for each name/service combination
      console.log('[Bluetooth] Requesting with filters...');
      
      const nameFilters = [
        { name: 'BT-58L' },
        { namePrefix: '802-TSC' },
      ];
      
      // For Windows compatibility, add service UUID to each name filter
      const filters = nameFilters.flatMap(nameFilter => 
        PRINTER_SERVICE_UUIDS.map(serviceUuid => ({
          ...nameFilter,
          services: [serviceUuid]
        }))
      );
      
      device = await navigator.bluetooth.requestDevice({
        filters,
        optionalServices,
      });
    }

    console.log('[Bluetooth] Device selected:', device.name);

    return {
      device,
      name: device.name || 'Unknown Device',
      id: device.id,
    };
  } catch (error) {
    console.error('[Bluetooth] Error:', error);
    throw error;
  }
}

/**
 * Connect to a Bluetooth printer device
 */
export async function connectToPrinter(
  device: BluetoothDevice
): Promise<BluetoothRemoteGATTServer | null> {
  try {
    if (!device.gatt) {
      console.error('[Bluetooth] Device does not support GATT');
      return null;
    }

    console.log('[Bluetooth] Connecting to GATT server...');
    const server = await device.gatt.connect();
    console.log('[Bluetooth] Connected to GATT server');
    
    return server;
  } catch (error) {
    console.error('[Bluetooth] Error connecting:', error);
    return null;
  }
}

/**
 * Disconnect from a Bluetooth printer device
 */
export function disconnectFromPrinter(device: BluetoothDevice): void {
  try {
    if (device.gatt?.connected) {
      device.gatt.disconnect();
    }
  } catch (error) {
    console.error('[Bluetooth] Error disconnecting:', error);
  }
}

/**
 * Find a writable characteristic on the connected device
 * Prefers characteristics that support regular write (more reliable on Windows)
 */
export async function findWriteCharacteristic(
  server: BluetoothRemoteGATTServer
): Promise<WriteCharacteristic | null> {
  console.log('[Bluetooth] Looking for writable characteristic...');
  
  const foundCharacteristics: BluetoothRemoteGATTCharacteristic[] = [];
  
  // First, try to get specific known printer services
  for (const serviceUuid of PRINTER_SERVICE_UUIDS) {
    try {
      console.log(`[Bluetooth] Trying service: ${serviceUuid}`);
      const service = await server.getPrimaryService(serviceUuid);
      const characteristics = await service.getCharacteristics();
      
      for (const characteristic of characteristics) {
        const props = characteristic.properties;
        console.log(`[Bluetooth] Characteristic ${characteristic.uuid}: write=${props.write}, writeWithoutResponse=${props.writeWithoutResponse}`);
        
        if (props.write || props.writeWithoutResponse) {
          foundCharacteristics.push(characteristic);
        }
      }
    } catch {
      // Service not available, try next
    }
  }

  // If no known service found, try getting all services
  if (foundCharacteristics.length === 0) {
    console.log('[Bluetooth] Known services not found, trying to enumerate all services...');
    try {
      const services = await server.getPrimaryServices();
      console.log(`[Bluetooth] Found ${services.length} services`);

      for (const service of services) {
        try {
          console.log(`[Bluetooth] Checking service: ${service.uuid}`);
          const characteristics = await service.getCharacteristics();

          for (const characteristic of characteristics) {
            const props = characteristic.properties;
            console.log(`[Bluetooth] Characteristic ${characteristic.uuid}: write=${props.write}, writeWithoutResponse=${props.writeWithoutResponse}`);
            
            if (props.write || props.writeWithoutResponse) {
              foundCharacteristics.push(characteristic);
            }
          }
        } catch {
          // Skip inaccessible services
        }
      }
    } catch (error) {
      console.warn('[Bluetooth] Could not enumerate all services:', error);
    }
  }

  if (foundCharacteristics.length === 0) {
    console.warn('[Bluetooth] No writable characteristic found');
    return null;
  }

  // Prefer characteristics that support regular write (more reliable on Windows)
  const preferWrite = foundCharacteristics.find(c => c.properties.write);
  if (preferWrite) {
    console.log(`[Bluetooth] Selected characteristic with write: ${preferWrite.uuid}`);
    return preferWrite;
  }

  // Fall back to writeWithoutResponse
  const first = foundCharacteristics[0];
  console.log(`[Bluetooth] Selected characteristic (writeWithoutResponse only): ${first.uuid}`);
  return first;
}

/**
 * Write data to a characteristic - uses writeWithoutResponse for speed
 */
export async function writeToCharacteristic(
  characteristic: WriteCharacteristic,
  data: Uint8Array | ArrayBuffer
): Promise<boolean> {
  const uint8Data = data instanceof Uint8Array ? data : new Uint8Array(data);
  
  try {
    // Prefer writeWithoutResponse for thermal printers (faster, no response wait)
    if (characteristic.properties.writeWithoutResponse) {
      await characteristic.writeValueWithoutResponse(uint8Data as BufferSource);
      return true;
    }
    
    // Fall back to regular write
    if (characteristic.properties.write) {
      await characteristic.writeValue(uint8Data as BufferSource);
      return true;
    }
    
    console.error('[Bluetooth] Characteristic does not support writing');
    return false;
  } catch (error) {
    console.error('[Bluetooth] Write error:', (error as Error)?.message);
    return false;
  }
}

/**
 * Send data to printer in chunks - simplified version
 */
export async function sendToPrinterInChunks(
  characteristic: WriteCharacteristic,
  data: Uint8Array,
  chunkSize = 200,
  delayMs = 10
): Promise<boolean> {
  const totalChunks = Math.ceil(data.length / chunkSize);
  console.log(`[Bluetooth] Sending ${data.length} bytes in ${totalChunks} chunks`);
  
  try {
    for (let i = 0; i < data.length; i += chunkSize) {
      const chunk = data.slice(i, Math.min(i + chunkSize, data.length));
      
      const success = await writeToCharacteristic(characteristic, chunk);
      if (!success) {
        console.error(`[Bluetooth] Write failed at byte ${i}`);
        return false;
      }

      // Small delay between chunks
      if (i + chunkSize < data.length && delayMs > 0) {
        await new Promise(resolve => setTimeout(resolve, delayMs));
      }
    }

    console.log('[Bluetooth] All data sent successfully');
    return true;
  } catch (error) {
    console.error('[Bluetooth] Error:', error);
    return false;
  }
}
